import { DocumentRepository } from "../../domain/repositories/DocumentRepository";
import {
  DocumentsResultEntity,
  UploadResultEntity,
  DocumentType,
  DocumentListFilters,
} from "../../domain/entities/Document";
import {
  uploadDocumentsApi,
  uploadStatementApi,
  fetchPendingDocumentsApi,
  fetchCompletedDocumentsApi,
} from "../api/documentsApi";
import { DocumentMapper } from "../mappers/DocumentMapper";

/**
 * Document Repository Implementation
 * Implements the DocumentRepository interface
 * Coordinates between API calls and mappers
 */
export class DocumentRepositoryImpl implements DocumentRepository {
  /**
   * Upload documents
   */
  async uploadDocuments(
    files: File[],
    documentType: DocumentType,
    companyId: string
  ): Promise<UploadResultEntity> {
    // Create FormData
    const formData = new FormData();
    formData.append(
      "document_type",
      documentType === "invoice" ? "invoice" : "bill"
    );
    formData.append("companyid", companyId);
    formData.append("folder_id", `folder_${companyId}`);
    formData.append("gpt_model", "gpt-4.1-mini");
    formData.append("process_immediately", "off");

    // Append all files
    files.forEach((file) => {
      formData.append("invoice_pdfs[]", file);
    });

    // Call API
    const response = await uploadDocumentsApi(formData);

    // Map to domain entity
    return DocumentMapper.toUploadResult(response);
  }

  /**
   * Upload bank/credit card statement
   */
  async uploadStatement(
    file: File,
    source: "bank" | "credit",
    bankName: string,
    zohoBankAccountId: string
  ): Promise<UploadResultEntity> {
    // Call API
    const response = await uploadStatementApi(
      file,
      source,
      bankName,
      zohoBankAccountId
    );

    // Map to domain entity
    return DocumentMapper.toUploadResult(response);
  }

  /**
   * Fetch pending documents
   */
  async fetchPendingDocuments(
    limit: number,
    offset: number,
    filters?: DocumentListFilters
  ): Promise<DocumentsResultEntity> {
    // Call API
    const response = await fetchPendingDocumentsApi(limit, offset, filters);

    // Map to domain entities
    return DocumentMapper.toPendingDocumentsResult(response);
  }

  /**
   * Fetch completed documents
   */
  async fetchCompletedDocuments(
    limit: number,
    offset: number,
    filters?: DocumentListFilters
  ): Promise<DocumentsResultEntity> {
    // Call API
    const response = await fetchCompletedDocumentsApi(limit, offset, filters);

    // Map to domain entities
    return DocumentMapper.toCompletedDocumentsResult(response);
  }
}
